import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WORKER_RATE_LABELS, type ExternalWorkerDetailDTO, type ExternalWorkerDTO } from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { HandCoins, Pencil, Plus, UserCog, Wrench } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { centsToPesosInput, pesosToCents, toInternalQty } from '../../lib/units';

const rateText = (w: Pick<ExternalWorkerDTO, 'rateType' | 'rateCents'>) =>
  `${formatMoney(w.rateCents)} ${w.rateType === 'PER_PIECE' ? 'por media res' : 'por kilo'}`;

/**
 * Despostero externo: no es empleado, cobra por media res o por kilo.
 * Se le anotan los trabajos (solos si se usa el Desposte, o a mano) y se le paga; en efectivo sale de la caja.
 */
export function WorkersPage() {
  const { can } = useSession();
  const canPay = can('suppliers.manage');
  const list = useQuery({ queryKey: ['workers'], queryFn: () => api.get<ExternalWorkerDTO[]>('/workers') });
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<ExternalWorkerDTO | 'new' | null>(null);
  const rows = list.data ?? [];

  return (
    <>
      <PageHeader
        title="Despostero"
        subtitle="El que viene a despostar y cobra por media res o por kilo. Anotás lo que hizo, ves cuánto se le debe y le pagás."
        actions={
          canPay && (
            <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
              Agregar despostero
            </Button>
          )
        }
      />
      <Card>
        <CardBody flush>
          {list.isPending ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<UserCog />}
              title="Todavía no cargaste ningún despostero"
              text="Cargalo con lo que cobra; después le anotás los trabajos y los pagos."
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Despostero</th>
                    <th style={{ textAlign: 'right' }}>Trabajos</th>
                    <th style={{ textAlign: 'right' }}>Se le debe</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w) => (
                    <tr key={w.id} data-clickable="true" onClick={() => setOpenId(w.id)} style={{ opacity: w.isActive ? 1 : 0.5 }}>
                      <td>
                        <span className="bf-cell-main">{w.name}</span> {!w.isActive && <Badge>Inactivo</Badge>}
                        <div className="bf-cell-sub">{[rateText(w), w.phone].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {w.jobs}
                        {w.lastJobAt && <div className="bf-cell-sub">último {formatDateTime(w.lastJobAt)}</div>}
                      </td>
                      <td className={`bf-num ${w.balanceCents > 0 ? 'bf-text-danger' : ''}`} style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatMoney(w.balanceCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {openId && <WorkerDialog id={openId} canPay={canPay} onClose={() => setOpenId(null)} onEdit={(w) => setEditing(w)} />}
      {editing && <WorkerFormDialog worker={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function WorkerDialog({ id, canPay, onClose, onEdit }: { id: string; canPay: boolean; onClose: () => void; onEdit: (w: ExternalWorkerDTO) => void }) {
  const q = useQuery({ queryKey: ['workers', id], queryFn: () => api.get<ExternalWorkerDetailDTO>(`/workers/${id}`) });
  const [mode, setMode] = useState<'job' | 'pay' | null>(null);
  const w = q.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={w?.name ?? 'Despostero'} description={w ? rateText(w) : undefined} wide>
      {!w ? (
        <Skeleton style={{ height: 160 }} />
      ) : mode ? (
        mode === 'job' ? (
          <JobForm worker={w} onDone={() => setMode(null)} />
        ) : (
          <PayForm worker={w} onDone={() => setMode(null)} />
        )
      ) : (
        <div className="bf-form">
          <div className="bf-close-summary">
            <div>
              <span>Se le debe</span>
              <strong className={`bf-num ${w.balanceCents > 0 ? 'bf-text-danger' : ''}`}>{formatMoney(w.balanceCents)}</strong>
            </div>
            <div>
              <span>Trabajos</span>
              <strong className="bf-num">{w.jobs}</strong>
            </div>
          </div>
          <div className="bf-form-actions" style={{ justifyContent: 'flex-start' }}>
            <Button icon={<Wrench />} onClick={() => setMode('job')}>
              Anotar trabajo
            </Button>
            {canPay && (
              <>
                <Button variant="primary" icon={<HandCoins />} onClick={() => setMode('pay')} disabled={w.balanceCents <= 0}>
                  Pagar
                </Button>
                <Button variant="ghost" icon={<Pencil />} onClick={() => onEdit(w)} style={{ marginLeft: 'auto' }}>
                  Editar
                </Button>
              </>
            )}
          </div>
          {w.movements.length === 0 ? (
            <p className="bf-cell-sub">Sin movimientos todavía.</p>
          ) : (
            <div className="bf-scroll-box bf-cut-table">
              <table className="bf-table">
                <tbody>
                  {w.movements.map((m) => (
                    <tr key={`${m.type}-${m.id}`}>
                      <td className="bf-cell-sub">{formatDateTime(m.createdAt)}</td>
                      <td>
                        <span className="bf-cell-main">{m.type === 'JOB' ? 'Trabajo' : 'Pago'}</span>
                        <div className="bf-cell-sub">{[m.description, m.createdByName].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td className={`bf-num ${m.amountCents < 0 ? 'bf-text-success' : ''}`} style={{ textAlign: 'right' }}>
                        {m.amountCents < 0 ? `− ${formatMoney(-m.amountCents)}` : formatMoney(m.amountCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

function useRefreshWorkers() {
  const qc = useQueryClient();
  return () => Promise.all(['workers', 'cash', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

function JobForm({ worker, onDone }: { worker: ExternalWorkerDTO; onDone: () => void }) {
  const refresh = useRefreshWorkers();
  const toast = useToast();
  const perPiece = worker.rateType === 'PER_PIECE';
  const [amount, setAmount] = useState(perPiece ? '1' : '');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pieces = perPiece ? Number(amount) || 0 : 0;
  const grams = perPiece ? 0 : (toInternalQty('WEIGHT', amount || '0') ?? 0);
  const charge = perPiece ? pieces * worker.rateCents : Math.round((grams * worker.rateCents) / 1000);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pieces <= 0 && grams <= 0) return setError(perPiece ? 'Poné cuántas medias reses.' : 'Poné cuántos kilos.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/workers/jobs', { workerId: worker.id, pieces: Math.round(pieces), grams, notes: notes.trim() || null });
      await refresh();
      toast.success('Trabajo anotado', `Se le suman ${formatMoney(charge)}.`);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="bf-form" onSubmit={submit} noValidate>
      {error && <Callout tone="danger">{error}</Callout>}
      <p className="bf-cell-sub" style={{ margin: 0 }}>
        Si usás el Desposte, los trabajos se anotan solos. Esto es para cuando despostó sin cargarlo en el sistema.
      </p>
      <Field label={perPiece ? 'Medias reses' : 'Kilos'} hint={`Cobra ${rateText(worker)}.`}>
        <Input className="bf-input--lg" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
      </Field>
      <Field label="Notas (opcional)">
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <div className="bf-form-actions">
        <strong className="bf-num" style={{ marginRight: 'auto' }}>
          {formatMoney(charge)}
        </strong>
        <Button onClick={onDone} disabled={busy}>
          Volver
        </Button>
        <Button type="submit" variant="primary" loading={busy}>
          Anotar
        </Button>
      </div>
    </form>
  );
}

function PayForm({ worker, onDone }: { worker: ExternalWorkerDTO; onDone: () => void }) {
  const refresh = useRefreshWorkers();
  const toast = useToast();
  const [amount, setAmount] = useState(centsToPesosInput(Math.max(worker.balanceCents, 0)));
  const [method, setMethod] = useState<'CASH' | 'TRANSFER' | 'OTHER'>('CASH');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const cents = pesosToCents(amount);
    if (!cents || cents <= 0) return setError('Poné cuánto le pagás.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/workers/payments', { workerId: worker.id, amountCents: cents, method, notes: notes.trim() || null });
      await refresh();
      toast.success('Pago registrado', method === 'CASH' ? 'Salió de la caja como retiro.' : undefined);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="bf-form" onSubmit={submit} noValidate>
      {error && <Callout tone="danger">{error}</Callout>}
      <div className="bf-form-row">
        <Field label="Monto" hint={`Se le debe ${formatMoney(worker.balanceCents)}.`}>
          <Input className="bf-input--lg" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </Field>
        <Field label="Cómo" hint={method === 'CASH' ? 'Sale de la caja abierta.' : undefined}>
          <Select value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
            <option value="CASH">Efectivo de la caja</option>
            <option value="TRANSFER">Transferencia</option>
            <option value="OTHER">Otro</option>
          </Select>
        </Field>
      </div>
      <Field label="Notas (opcional)">
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <div className="bf-form-actions">
        <Button onClick={onDone} disabled={busy}>
          Volver
        </Button>
        <Button type="submit" variant="primary" icon={<HandCoins />} loading={busy}>
          Pagar
        </Button>
      </div>
    </form>
  );
}

function WorkerFormDialog({ worker, onClose }: { worker: ExternalWorkerDTO | null; onClose: () => void }) {
  const refresh = useRefreshWorkers();
  const [name, setName] = useState(worker?.name ?? '');
  const [phone, setPhone] = useState(worker?.phone ?? '');
  const [rateType, setRateType] = useState<'PER_PIECE' | 'PER_KG'>(worker?.rateType ?? 'PER_PIECE');
  const [rate, setRate] = useState(worker ? centsToPesosInput(worker.rateCents) : '');
  const [active, setActive] = useState(worker?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const cents = pesosToCents(rate || '0') ?? 0;
    setBusy(true);
    setError(null);
    try {
      const body = { name: name.trim(), phone: phone.trim() || null, rateType, rateCents: cents, isActive: active };
      if (worker) await api.put(`/workers/${worker.id}`, body);
      else await api.post('/workers', body);
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} title={worker ? 'Editar despostero' : 'Agregar despostero'}>
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Nombre">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Teléfono (opcional)">
          <Input inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <div className="bf-form-row">
          <Field label="Cobra">
            <Select value={rateType} onChange={(e) => setRateType(e.target.value as typeof rateType)}>
              {Object.entries(WORKER_RATE_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={rateType === 'PER_PIECE' ? '$ por media res' : '$ por kilo'} hint="Si no cobra, dejalo en 0.">
            <Input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
          </Field>
        </div>
        {worker && (
          <label className="bf-check">
            <Switch checked={active} onCheckedChange={setActive} label="Activo" /> Activo
          </label>
        )}
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            Guardar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
